/* eslint-disable no-use-before-define */
import { $isListItemNode, ListNode, ListType, SerializedListNode } from '@lexical/list';
import { $applyNodeReplacement, EditorConfig, LexicalEditor, LexicalNode, LexicalUpdateJSON, NodeKey } from 'lexical';
import { BrandColorRef } from 'modules/BrandDefinition';
import { SerializedCustomListItemNode } from './CustomListItemNode';

const NODE_TYPE = 'custom-list';

function $isNestedList(list: CustomListNode): boolean {
  return $isListItemNode(list.getParent());
}

function setPropertyForBulletList(element: HTMLElement, colorHEX: string | undefined): void {
  const CSS_PROPERTY_FOR_BULLET_COLOR = '--bullet-color';

  element.style.setProperty(CSS_PROPERTY_FOR_BULLET_COLOR, colorHEX ?? null);
}

export type SerializedCustomListNode = SerializedListNode & {
  type: typeof NODE_TYPE;
  children: SerializedCustomListItemNode[];
  color: BrandColorRef | undefined;
};

export class CustomListNode extends ListNode {
  __color?: BrandColorRef;

  static getType(): typeof NODE_TYPE {
    return NODE_TYPE;
  }

  constructor(listType: ListType, start: number, color?: BrandColorRef, key?: NodeKey) {
    super(listType, start, key);
    this.__color = color;
  }

  static clone(node: CustomListNode): CustomListNode {
    return new CustomListNode(node.__listType, node.__start, node.__color, node.__key);
  }

  setColor(color: BrandColorRef | undefined): this {
    this.getWritable().__color = color;

    return this;
  }

  getColor(): BrandColorRef | undefined {
    return this.getLatest().__color;
  }

  createDOM(config: EditorConfig, _editor?: LexicalEditor): HTMLElement {
    const element = super.createDOM(config, _editor);
    if (!$isNestedList(this)) {
      setPropertyForBulletList(element, this.__color?.HEX);
    }

    return element;
  }

  updateDOM(prevNode: CustomListNode, dom: HTMLElement, config: EditorConfig): boolean {
    const isUpdated = super.updateDOM(prevNode as this, dom, config);
    if (!$isNestedList(this)) {
      setPropertyForBulletList(dom, this.__color?.HEX);
    }

    return isUpdated;
  }

  exportJSON(): SerializedCustomListNode {
    const data = super.exportJSON() as unknown as SerializedCustomListNode;
    data.type = CustomListNode.getType();
    data.color = this.getColor();

    return data;
  }

  updateFromJSON(
    serializedNode: LexicalUpdateJSON<SerializedCustomListNode>,
  ): this {
    return this
      .setColor(serializedNode.color);
  }

  static importJSON(serializedNode: SerializedCustomListNode): CustomListNode {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define, no-use-before-define
    return $createCustomListNode(serializedNode.listType, serializedNode.start, serializedNode.color);
  }
}

export function $isCustomListNode(node: LexicalNode | null | undefined): node is CustomListNode {
  return node instanceof CustomListNode;
}

export function $createCustomListNode(listType: ListType, start: number, color?: BrandColorRef): CustomListNode {
  return $applyNodeReplacement(new CustomListNode(listType, start, color));
}
